import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { EnquiryCartProvider } from "./context/EnquiryCartContext";
import { SEOProvider } from "./context/SEOContext";
import { ThemeProvider } from "./context/ThemeContext";
import ScrollToTop from "./components/ScrollToTop";

// Lazy load pages for better performance
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Catalogue = lazy(() => import("./pages/Catalogue"));
const EnquiryCart = lazy(() => import("./pages/EnquiryCart"));
const MyEnquiries = lazy(() => import("./pages/MyEnquiries"));
const AdminCRM = lazy(() => import("./pages/AdminCRM"));
const Profile = lazy(() => import("./pages/Profile"));
const Blogs = lazy(() => import("./pages/Blogs"));
const Calculator = lazy(() => import("./pages/Calculator"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const WhyTrustUs = lazy(() => import("./pages/WhyTrustUs"));

// Loading spinner component
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="spinner" />
    </div>
  );
}

// Protected Route Component - Redirects to login with return URL
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  
  return children;
}

// Admin Route
function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/catalogue" replace />;
  }
  
  return children;
}

// Public Route - NO auto-redirect if logged in (allows browsing)
function AuthRoute({ children }) {
  const { loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes - Browsable without login */}
        <Route path="/" element={<Landing />} />
        <Route path="/catalogue" element={<Catalogue />} />
        <Route path="/products" element={<Navigate to="/catalogue" replace />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/calculator" element={<Calculator />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/why-trust-us" element={<WhyTrustUs />} />
        
        {/* Auth Routes - Login page */}
        <Route path="/login" element={
          <AuthRoute>
            <Login />
          </AuthRoute>
        } />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        
        {/* Protected Routes - Require login */}
        <Route path="/enquiry-cart" element={<EnquiryCart />} />
        <Route path="/my-enquiries" element={
          <ProtectedRoute>
            <MyEnquiries />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminCRM />
          </AdminRoute>
        } />
        <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-black text-primary mb-4">404</h1>
        <p className="text-xl text-muted-foreground mb-6">Page not found</p>
        <a href="/" className="btn-steel inline-block px-6 py-3">
          Go Home
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <SEOProvider>
          <AuthProvider>
            <EnquiryCartProvider>
              <BrowserRouter>
                <ScrollToTop />
                <AppRoutes />
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      color: 'hsl(var(--foreground))',
                      marginTop: '70px',
                    },
                  }}
                />
              </BrowserRouter>
            </EnquiryCartProvider>
          </AuthProvider>
        </SEOProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;
